// Line tags: what a meeting said, as facts about subjects (the line-tag
// design, owner decision 2026-10-16).
//
// The transcript is never edited (standoff annotation): a fact is a separate
// record that points at one line (`seq`) and, when the line speaks about
// several subjects, at its own part of the line (`span`). The pipeline is
// pipes and filters:
//
//   inventory (model)  a catalogue of subjects + typed edges     ModelSubject, ModelEdge
//   freeze (code)      checked, re-keyed to opaque ids, frozen   Catalogue      (`ground.ts`)
//   tag (model)        per window: each own line noise or links  TagLine        (map)
//   ground (code)      every link checked, quotes cut by code    Fact[]         (`ground.ts`, reduce)
//   assemble (code)    every fact of one work item, meeting-wide WorkItem       (this file)
//   classify (code)    named rules + side rules                  ItemOutcome    (`specs.ts`)
//
// The model-facing schemas live here, not in the workflows, because the
// code that grounds their answers (`ground.ts`) is shared and pure; both
// workflows import them, the `TopicSignalsSchema` precedent.
//
// Pure, browser-safe: no I/O, no regex lookbehind.

import { z } from "zod";

import { ABOUT_THIS_PROJECT, QuoteSchema } from "./triage.js";
import {
  COM_BY,
  COM_TARGETS,
  EDGE_KINDS,
  FACT_FUNCTIONS,
  REF_KINDS,
  SUBJECT_KINDS,
  type ComBy,
  type ComTarget,
  type DoubtBranch,
  type DoubtCode,
  type EdgeKind,
  type FactFunction,
  type RefKind,
  type SubjectKind,
} from "./vocabulary.js";
import type { Classification } from "./triage.js";

export type AboutThisProject = (typeof ABOUT_THIS_PROJECT)[number];

// ---------------------------------------------------------------------------
// What the models answer (strict: no record, no passthrough)
// ---------------------------------------------------------------------------

/** One subject as the inventory (or a tag window) proposes it. */
export const ModelSubjectSchema = z
  .object({
    /** The model's own slug; code replaces it with an opaque key. */
    key: z.string(),
    /** A facet's work item; null for everything else. */
    parentKey: z.string().nullable(),
    kind: z.enum(SUBJECT_KINDS),
    /** A short name in the meeting's language (sealed, 30 days). */
    label: z.string(),
    /** The line that says what it is. */
    anchor: QuoteSchema,
    aboutThisProject: z.enum(ABOUT_THIS_PROJECT),
    /** The OPEN snapshot issue it is, per subject and per facet. */
    existingIid: z.number().int().nullable(),
  })
  .strict();
export type ModelSubject = z.infer<typeof ModelSubjectSchema>;

/** A typed link between two subjects, with the line that says so. */
export const ModelEdgeSchema = z
  .object({
    from: z.string(),
    to: z.string(),
    kind: z.enum(EDGE_KINDS),
    evidence: QuoteSchema,
  })
  .strict();
export type ModelEdge = z.infer<typeof ModelEdgeSchema>;

/** One link of one line to one subject. */
export const TagLinkSchema = z
  .object({
    /** A catalogue key (or a key of this window's `newSubjects`). */
    subject: z.string(),
    /** The kinds of information, the dominant one first. */
    fns: z.array(z.enum(FACT_FUNCTIONS)),
    /** This link's own part of the line; required (checked after) when the
     *  line has two or more links. */
    span: z.string().nullable(),
    comTarget: z.enum(COM_TARGETS).nullable(),
    comBy: z.enum(COM_BY).nullable(),
    ref: z.enum(REF_KINDS).nullable(),
  })
  .strict();
export type TagLink = z.infer<typeof TagLinkSchema>;

/** One own line of a tag window: noise, or one to three links. */
export const TagLineSchema = z
  .object({
    segId: z.string(),
    noise: z.boolean(),
    links: z.array(TagLinkSchema),
  })
  .strict();
export type TagLine = z.infer<typeof TagLineSchema>;

/** A tag window's answer. */
export const TagAnswerSchema = z
  .object({
    lines: z.array(TagLineSchema),
    /** Subjects the inventory missed, anchored in this window's own lines. */
    newSubjects: z.array(ModelSubjectSchema),
  })
  .strict();
export type TagAnswer = z.infer<typeof TagAnswerSchema>;

/** A line holds at most this many subject links. */
export const MAX_LINKS_PER_LINE = 3;

// ---------------------------------------------------------------------------
// The frozen catalogue and the grounded facts
// ---------------------------------------------------------------------------

/** A part of one stored line, by UTF-16 offsets into its text; end exclusive. */
export interface Span {
  readonly start: number;
  readonly end: number;
}

/** A subject after freezing: opaque key (`s07`, a facet `s07.2`), the line
 *  that anchors it, and an issue number only when it is open in the
 *  project snapshot. */
export interface Subject {
  readonly key: string;
  readonly parentKey: string | null;
  readonly kind: SubjectKind;
  readonly label: string;
  readonly anchorSeq: number;
  /** Where the anchor quote sits in its line; null when it was not found. */
  readonly anchorSpan: Span | null;
  readonly aboutThisProject: AboutThisProject;
  readonly existingIid: number | null;
  /** Open issues it continues, builds on or changes (the inventory's, checked
   *  by the freeze), ascending; absent when none. */
  readonly relatedIids?: readonly number[];
}

/** A typed link between two subjects of the catalogue. */
export interface Edge {
  readonly from: string;
  readonly to: string;
  readonly kind: EdgeKind;
  readonly evidenceSeq: number;
  readonly evidenceSpan: Span | null;
}

/** The catalogue the tag windows join against. Frozen: a window never
 *  changes it, so the reduce is a plain join with no merge call. */
export interface Catalogue {
  readonly subjects: readonly Subject[];
  /** Candidate edges; `groundEdges` keeps the ones the tags back. */
  readonly edges: readonly Edge[];
}

/** The attribution key: one line, one subject. */
export type FactId = `${string}#${string}`;

/** What one line says about one subject. */
export interface Fact {
  readonly id: FactId;
  readonly seq: number;
  readonly subject: string;
  readonly fns: readonly FactFunction[];
  /** This fact's part of the line; null = the whole line. */
  readonly span: Span | null;
  /** The line has two or more links and this one has no part of its own:
   *  it counts as evidence for each, but only the main link (`rank` 0) can
   *  create an outcome. */
  readonly shared: boolean;
  /** Position among the line's kept links; 0 is the line's main link. */
  readonly rank: number;
  readonly com: { readonly target: ComTarget; readonly by: ComBy } | null;
  readonly ref: RefKind | null;
}

/** The id of the fact one line gives about one subject. */
export const factIdOf = (segId: string, subjectKey: string): FactId => `${segId}#${subjectKey}`;

/** May this fact create an outcome (an outcome facet, a commitment)? A
 *  shared fact only for the line's main link. */
export const isOutcomeEligible = (f: Fact): boolean => !f.shared || f.rank === 0;

/** The text a fact cites, cut from the stored line by code: its span, or
 *  the whole line. So every quote is valid by construction. */
export const quoteOf = (f: Pick<Fact, "span">, lineText: string): string =>
  f.span === null ? lineText : lineText.slice(f.span.start, f.span.end);

/** Facts in transcript order: by line, then the line's link order. */
export const byPosition = (a: Fact, b: Fact): number => a.seq - b.seq || a.rank - b.rank;

// ---------------------------------------------------------------------------
// Work items: everything said about one subject, from the whole meeting
// ---------------------------------------------------------------------------

/** A stretch of consecutive lines, by `seq`, inclusive. */
export interface SeqRun {
  readonly from: number;
  readonly to: number;
}

/** A fact that reaches an item through a grounded edge: context only. */
export interface SupportingFact {
  readonly fact: Fact;
  readonly via: EdgeKind;
}

export interface WorkItem {
  /** The root's opaque key. */
  readonly itemKey: string;
  readonly root: Subject;
  readonly facets: readonly Subject[];
  /** Facts linked to the root or a facet: they decide the class, may be
   *  stored quotes and refine evidence. */
  readonly primary: readonly Fact[];
  /** Facts of linked subjects: draft context only, never stored or counted. */
  readonly supporting: readonly SupportingFact[];
  /** The lines of the primary facts as stretches, for display and promote. */
  readonly runs: readonly SeqRun[];
}

/** Lines in ascending stretches; neighbouring lines join one stretch. */
export const runsOf = (seqs: Iterable<number>): SeqRun[] => {
  const sorted = [...new Set(seqs)].sort((a, b) => a - b);
  const out: SeqRun[] = [];
  for (const s of sorted) {
    const last = out[out.length - 1];
    if (last !== undefined && s === last.to + 1) out[out.length - 1] = { from: last.from, to: s };
    else out.push({ from: s, to: s });
  }
  return out;
};

/**
 * Which of a linked subject's facts an edge lends as context, beyond the
 * ones on the edge's own line: the kinds of information the link is about.
 */
export const EDGE_CONTEXT_FUNCTIONS: Readonly<Record<EdgeKind, readonly FactFunction[]>> = {
  prioritizes: ["PRIO"],
  depends_on: ["REQ", "BUG", "STAT", "DONE"],
  alternative_to: ["DEC", "REJ"],
  means_for: ["REQ", "AC"],
  status_of: ["STAT", "DONE"],
  advice_on: ["CTX", "CON"],
  reply_owed: ["COM", "Q"],
};

/**
 * Collect every fact per work item, from the whole meeting (the reduce):
 * - one item per `work` root; `source` and `process` subjects never file and
 *   become no item (their commitments and questions are meeting-level,
 *   `specs.ts` `meetingSidecars`);
 * - primary: the facts on the root and on its facets;
 * - supporting: for every grounded edge with one end in the item, the other
 *   end's facts on the edge's line, plus its facts of the kinds the edge is
 *   about (`EDGE_CONTEXT_FUNCTIONS`); never a fact that is already primary.
 * Items come back in the catalogue's order.
 */
export const assembleWorkItems = (
  catalogue: Pick<Catalogue, "subjects">,
  facts: readonly Fact[],
  edges: readonly Edge[],
): WorkItem[] => {
  const facetsOf = new Map<string, Subject[]>();
  for (const s of catalogue.subjects) {
    if (s.kind !== "facet" || s.parentKey === null) continue;
    const list = facetsOf.get(s.parentKey) ?? [];
    list.push(s);
    facetsOf.set(s.parentKey, list);
  }
  const factsOf = new Map<string, Fact[]>();
  for (const f of facts) {
    const list = factsOf.get(f.subject) ?? [];
    list.push(f);
    factsOf.set(f.subject, list);
  }
  /** A subject with its facets: an edge to a facet reaches its item's facts. */
  const familyOf = (key: string): string[] => [key, ...(facetsOf.get(key) ?? []).map((s) => s.key)];

  return catalogue.subjects
    .filter((s) => s.kind === "work")
    .map((root): WorkItem => {
      const facets = facetsOf.get(root.key) ?? [];
      const own = new Set([root.key, ...facets.map((f) => f.key)]);
      const primary = [...own].flatMap((k) => factsOf.get(k) ?? []).sort(byPosition);
      const primaryIds = new Set(primary.map((f) => f.id));

      const supporting = new Map<FactId, SupportingFact>();
      for (const e of edges) {
        const fromIn = own.has(e.from);
        const toIn = own.has(e.to);
        if (fromIn === toIn) continue;
        const other = fromIn ? e.to : e.from;
        const lends = EDGE_CONTEXT_FUNCTIONS[e.kind];
        for (const f of familyOf(other).flatMap((k) => factsOf.get(k) ?? [])) {
          if (primaryIds.has(f.id) || supporting.has(f.id)) continue;
          if (f.seq === e.evidenceSeq || f.fns.some((fn) => lends.includes(fn))) {
            supporting.set(f.id, { fact: f, via: e.kind });
          }
        }
      }

      return {
        itemKey: root.key,
        root,
        facets,
        primary,
        supporting: [...supporting.values()].sort((a, b) => byPosition(a.fact, b.fact)),
        runs: runsOf(primary.map((f) => f.seq)),
      };
    });
};

// ---------------------------------------------------------------------------
// What classification gives an item
// ---------------------------------------------------------------------------

/** What an item gives besides its main outcome; each from its own side rule. */
export type Sidecar =
  | { readonly kind: "status_comment"; readonly iid: number; readonly facts: readonly FactId[] }
  | { readonly kind: "decision"; readonly facts: readonly FactId[] }
  | { readonly kind: "question"; readonly fact: FactId; readonly scope: "item" | "meeting" }
  /** A commitment to reply or about the process, for the meeting's list. */
  | { readonly kind: "commitment"; readonly fact: FactId }
  /** `branch`: which branch of the rule raised it (content-free, for counts). */
  | {
      readonly kind: "doubt";
      readonly code: DoubtCode;
      readonly subjects: readonly string[];
      readonly branch?: DoubtBranch;
    };

export interface ItemOutcome {
  readonly primary: Classification;
  /** The name of the rule that decided it (content-free, for counts). */
  readonly rule: string;
  /** The kinds of information that decided it: they pick the stored quotes. */
  readonly decides: readonly FactFunction[];
  readonly sidecars: readonly Sidecar[];
}

/** An item a doubt names waits for the owner's answer; the rest of the
 *  meeting goes on (owner decision 2026-10-16, 2a). */
export const isHeld = (o: Pick<ItemOutcome, "sidecars">): boolean =>
  o.sidecars.some((s) => s.kind === "doubt");
